import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  BasketPricesDto,
  BasketPricesPayload,
  DomainUpdateEventResultInterface,
  ProvisionalDatesRetraitDto,
} from '../interfaces/domainUpdateEventResult-interface';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from './configuration.service';
import { DomainUpdateStatus } from '../enums/enumerations';
import { EstimatePricesRequest, EstimatePricesResult } from '../interfaces/product-interface';
import { AnyValue } from '@shared/types/any-value.type';

@Injectable({
  providedIn: 'root',
})
export class DomainUpdateService {
  COMPANIES_ROUTE_API_v2 = 'api/v2.0/companies';
  COMMAND_PUBLIC_URL_API = 'api-public/v1.0';

  public _basketPricesSubject$ = new BehaviorSubject<BasketPricesDto>({
    tva: 0,
    totalHT: 0,
    totalTTC: 0,
    totalTVA: 0,
  });

  public basketPrices$: Observable<BasketPricesDto> = this._basketPricesSubject$.asObservable();

  public _estimatedPricesSubject$ = new BehaviorSubject<EstimatePricesResult>({} as EstimatePricesResult);
  public estimatedPrices$: Observable<EstimatePricesResult> = this._estimatedPricesSubject$.asObservable();

  public serviceProcessing: boolean = false;

  public domainUpdateEvents$ = new BehaviorSubject<DomainUpdateEventResultInterface[]>([]);

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigurationService,
  ) {}

  public getDomainUpdateEvents(): Observable<DomainUpdateEventResultInterface[]> {
    return this.domainUpdateEvents$.asObservable();
  }

  public submitDomainUpdateEvent(
    domainId: string,
    payload: DomainUpdateEventResultInterface,
  ): Observable<DomainUpdateEventResultInterface> {
    return this.httpClient.post<DomainUpdateEventResultInterface>(
      `${this.configService.urlApiSiVision}/${this.COMPANIES_ROUTE_API_v2}/${domainId}/UpdateEvents`,
      payload,
    );
  }

  public getDomainUpdateEventsByStatus(
    domainId: string,
    status: DomainUpdateStatus,
    platformOrigin: number | null = null,
  ): Observable<DomainUpdateEventResultInterface[]> {
    let params = {};
    if (platformOrigin) {
      params = {
        platformOrigin: platformOrigin,
      };
    }
    return this.httpClient
      .get<DomainUpdateEventResultInterface[]>(
        `${this.configService.urlApiSiVision}/${this.COMPANIES_ROUTE_API_v2}/${domainId}/UpdateEvents/${status}`,
        {
          params,
        },
      )
      .pipe(
        tap((events: DomainUpdateEventResultInterface[]) => {
          if (events) {
            this.domainUpdateEvents$.next(events);
          }
        }),
      );
  }

  public validateDomainUpdateEvent(domainId: string, domainUpdateEventId: string): Observable<AnyValue> {
    return this.httpClient.patch<AnyValue>(
      `${this.configService.urlApiSiVision}/${this.COMPANIES_ROUTE_API_v2}/${domainId}/UpdateEvents/${domainUpdateEventId}/validate`,
      null,
    );
  }

  public deleteDomainUpdateEvent(domainId: string, domainUpdateEventId: string): Observable<AnyValue> {
    return this.httpClient.delete<AnyValue>(
      `${this.configService.urlApiSiVision}/${this.COMPANIES_ROUTE_API_v2}/${domainId}/UpdateEvents/${domainUpdateEventId}`,
    );
  }

  public domainUpdateEventOrder(domainId: string, domainUpdateEventId: string, orderId: string): Observable<AnyValue> {
    return this.httpClient.patch<AnyValue>(
      `${this.configService.urlApiSiVision}/${this.COMPANIES_ROUTE_API_v2}/${domainId}/UpdateEvents/${domainUpdateEventId}`,
      {
        domainUpdateEventId: domainUpdateEventId,
        orderId: orderId,
      },
    );
  }

  public getPricesAndTvaAmount(payload: BasketPricesPayload) {
    return this.httpClient
      .post<BasketPricesDto>(`${this.configService.urlApiMsOrder}/${this.COMMAND_PUBLIC_URL_API}/CalculPrices`, payload)
      .pipe(
        tap((basketPrices) => {
          this._basketPricesSubject$.next(basketPrices);
        }),
      );
  }

  public getProvisionalDatesAndInformationsSB(domainId: string): Observable<ProvisionalDatesRetraitDto> {
    return this.httpClient.get<ProvisionalDatesRetraitDto>(
      `${this.configService.urlApiSiVision}/${this.COMPANIES_ROUTE_API_v2}/${domainId}/ProvisionalDatesAndInformationsSB`,
    );
  }

  public resetAllDomainUpdateSubjects() {
    this._basketPricesSubject$.next({
      tva: 0,
      totalHT: 0,
      totalTTC: 0,
      totalTVA: 0,
    });
    this.domainUpdateEvents$.next([]);
    this._estimatedPricesSubject$.next({} as EstimatePricesResult);
  }

  public getEstimatedPrices(payload: EstimatePricesRequest) {
    return this.httpClient
      .post<EstimatePricesResult>(
        `${this.configService.urlApiSiVision}/${this.COMPANIES_ROUTE_API_v2}/estimatePrices`,
        payload,
      )
      .pipe(
        tap((estimatedPrices) => {
          this._estimatedPricesSubject$.next(estimatedPrices);
        }),
      );
  }
}
